import {
  Box,
  Button,
  Group,
  Pill,
  rem,
  ScrollArea,
  SegmentedControl,
  Space,
  Stack,
  Tooltip,
} from '@mantine/core';
import CustomSpinner from '@/components/Spinner.tsx';
import StepExecutionsRenderer from '@/pages/Run/components/StepExecutionsRenderer.tsx';
import React, { RefObject, useEffect, useMemo, useState } from 'react';
import RunSummaryCard from './components/RunSummaryCard';
import { useStepContext } from '@/context/StepContext/useStepContext.ts';
import { useRunSSE } from '@/hooks/api/sse/useRunSSE.ts';
import { notifications } from '@mantine/notifications';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAutomationId from '@/hooks/useAutomationId.ts';
import useRetrieveRunWithDatasets from '@/hooks/api/runs/useRetrieveRunWithDatasets.ts';
import useRetrieveRunAutomationVersion from '@/hooks/api/runs/useRetrieveRunAutomationVersion.ts';
import { indexStepsById } from '@/utils/stepUtils.ts';
import useRunId from '@/hooks/useRunId.ts';
import { watchRun } from '@/features/runBrowserNotifications/watchedRuns.ts';
import HorizontalRunScopeView from '@/pages/Run/components/ScopeStack/HorizontalRunScopeView.tsx';
import useRetrieveAutoExpandPlan from '@/hooks/api/runs/useRetrieveAutoExpandPlan.ts';
import { AutoExpandPlan } from '@/types/autoExpandPlan.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import { buildRunPresentationSteps } from '@/utils/runStepPresentation.ts';

const RunPage = () => {
  const { isConnected, isDisabled: isSSEDisabled } = useRunSSE();

  const {
    data: runWithDatasets,
    isPending: loadingRun,
    error: runError,
  } = useRetrieveRunWithDatasets();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const automationId = useAutomationId();
  const horizontalScopeStackEnabled = searchParams.get('execution_view') === 'finder';
  const runSummaryWidth = rem(500);
  const executionView = horizontalScopeStackEnabled ? 'finder' : 'standard';

  const setExecutionView = (nextView: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (nextView === 'finder') {
        next.set('execution_view', 'finder');
      } else {
        next.delete('execution_view');
      }
      return next;
    });
  };

  useEffect(() => {
    if (runError?.status === 404) {
      navigate(`/automations/${automationId}/runs`, { replace: true });
      notifications.show({
        title: 'Run Not Found',
        message: 'The requested run does not exist.',
        color: 'yellow',
      });
    }
  }, [automationId, navigate, runError]);

  const { scrollableRef } = useStepContext();
  const runId = useRunId();

  const { data: automationVersion, isPending: loadingAutomationVersion } =
    useRetrieveRunAutomationVersion(runId);

  const stepsById = useMemo(() => {
    if (!automationVersion) return {};
    return indexStepsById(buildRunPresentationSteps(automationVersion));
  }, [automationVersion]);

  const [showDisconnectedWarning, setShowDisconnectedWarning] = useState(false);
  const [autoExpandPlan, setAutoExpandPlan] = useState<AutoExpandPlan | null>(null);
  const { refetch: retrieveAutoExpandPlan, isFetching: loadingAutoExpandPlan } =
    useRetrieveAutoExpandPlan();

  const handleAutoExpand = async () => {
    const result = await retrieveAutoExpandPlan();
    if (result.error || !result.data) {
      notifications.show({
        title: 'Unable to expand results',
        message: 'Please try again.',
        color: 'red',
      });
      return;
    }
    setAutoExpandPlan({ ...result.data });
    if (result.data.mode === 'none') {
      notifications.show({
        title: 'No relevant results',
        message:
          'No paused, failed, partially completed, or running step results are available to expand.',
        color: 'gray',
      });
    } else if (result.data.hidden_root_result_count > 0) {
      notifications.show({
        title: 'Relevant results expanded',
        message: `Expanded ${result.data.step_run_ids_to_open.length} of ${result.data.matching_result_count} relevant root steps.`,
        color: 'blue',
      });
    } else if (result.data.child_results_skipped_for_size) {
      notifications.show({
        title: 'Parent result expanded',
        message: 'Open Children Step Results to review this large group.',
        color: 'blue',
      });
    }
  };

  useEffect(() => {
    if (isConnected || isSSEDisabled) {
      setShowDisconnectedWarning(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      setShowDisconnectedWarning(true);
    }, 3000);

    return () => window.clearTimeout(timeout);
  }, [isConnected, isSSEDisabled]);

  useEffect(() => {
    if (!runWithDatasets?.run) return;

    watchRun({
      automationId,
      run: runWithDatasets.run,
    });
  }, [automationId, runWithDatasets?.run]);

  if (loadingRun || !runWithDatasets || loadingAutomationVersion) {
    return <CustomSpinner />;
  }

  const runViewControls = (
    <>
      <Space h="20" w="100%" />
      {showDisconnectedWarning && (
        <Group justify="center">
          <Tooltip label="Reconnecting automatically" openDelay={200}>
            <Pill
              size="sm"
              styles={{
                root: {
                  backgroundColor: '#FFF4E5',
                  color: '#9A4D00',
                  border: '1px solid #FFD8A8',
                  fontWeight: 600,
                },
              }}
            >
              Live updates paused
            </Pill>
          </Tooltip>
        </Group>
      )}
      {horizontalScopeStackEnabled ? (
        <Box w={runSummaryWidth} maw="100%" mx="auto">
          <RunSummaryCard runWithDatasets={runWithDatasets} />
        </Box>
      ) : (
        <RunSummaryCard runWithDatasets={runWithDatasets} />
      )}
      {runWithDatasets.run.status !== ExecutionStatus.Completed && (
        <Group justify="center">
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            onClick={handleAutoExpand}
            loading={loadingAutoExpandPlan}
          >
            Auto-expand
          </Button>
        </Group>
      )}
      <Box w={runSummaryWidth} maw="100%" mx="auto">
        <SegmentedControl
          aria-label="Run results view"
          value={executionView}
          onChange={setExecutionView}
          data={[
            { label: 'Standard', value: 'standard' },
            { label: 'Scope navigator', value: 'finder' },
          ]}
          size="xs"
          fullWidth
        />
      </Box>
    </>
  );

  if (horizontalScopeStackEnabled) {
    return (
      <Box h="100%" mih={0} w="100%" px="md" pb="md">
        <HorizontalRunScopeView
          key={runWithDatasets.run.id}
          run={runWithDatasets.run}
          stepsById={stepsById}
          autoExpandPlan={autoExpandPlan}
          scrollableRef={scrollableRef as RefObject<HTMLDivElement>}
          scrollingHeader={
            <Stack w="100%" gap={10} pb="md">
              {runViewControls}
            </Stack>
          }
        />
      </Box>
    );
  }

  return (
    <ScrollArea w="100%" h="100%" ref={scrollableRef as RefObject<HTMLDivElement>}>
      <Group justify="center" w="100%">
        <Stack
          h="100%"
          w={runSummaryWidth}
          maw="100%"
          miw={0}
          gap={10}
          px="md"
          py="lg"
          justify="center"
        >
          {runViewControls}
          <Space h="60" w="100%" />

          <StepExecutionsRenderer
            run={runWithDatasets.run}
            enabled={true}
            parentSteps={[]}
            stepsById={stepsById}
            parentPath={undefined}
            childIndex={undefined}
            autoExpandPlan={autoExpandPlan}
          />

          <Space h="xl" w="100%" />
        </Stack>
      </Group>
    </ScrollArea>
  );
};

export default RunPage;
